import React from 'react';
import Box from '@mui/joy/Box';
import Alert from '@mui/joy/Alert';
import Snackbar from '@mui/joy/Snackbar';
import IconButton from '@mui/joy/IconButton';
import Typography from '@mui/joy/Typography';

// Icons
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';

import { useNotification } from '../../hooks/useNotification';
import type { Notification } from '../../contexts/NotificationContext';

const getNotificationIcon = (type: Notification['type']) => {
  switch (type) {
    case 'success':
      return <CheckCircleIcon />;
    case 'error':
      return <ErrorIcon />;
    case 'warning':
      return <WarningIcon />;
    case 'info':
      return <InfoIcon />;
    default:
      return <InfoIcon />;
  }
};

const getNotificationColor = (type: Notification['type']) => {
  switch (type) {
    case 'success':
      return 'success';
    case 'error':
      return 'danger';
    case 'warning':
      return 'warning';
    case 'info':
      return 'primary';
    default:
      return 'neutral';
  }
};

interface NotificationItemProps {
  notification: Notification;
  onClose: (id: string) => void;
  index: number;
}

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onClose,
  index
}) => {
  const handleClose = () => {
    onClose(notification.id);
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 24 + (index * 80), // Espaçamento entre notificações
        right: 24,
        zIndex: 1400 + index,
        maxWidth: 400,
        minWidth: 300,
        transform: 'translateZ(0)', // Força aceleração de hardware
        animation: 'slideInRight 0.3s ease-out',
        '@keyframes slideInRight': {
          '0%': {
            transform: 'translateX(100%)',
            opacity: 0
          },
          '100%': {
            transform: 'translateX(0)',
            opacity: 1
          }
        }
      }}
    >
      <Alert
        variant="solid"
        color={getNotificationColor(notification.type)}
        startDecorator={notification.icon || getNotificationIcon(notification.type)}
        endDecorator={
          <IconButton
            variant="plain"
            size="sm"
            color="neutral"
            onClick={handleClose}
            sx={{ 
              color: 'inherit',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.2)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        }
        sx={{
          boxShadow: 'lg',
          backdropFilter: 'blur(8px)'
        }}
      >
        <Box>
          <Typography level="title-sm" sx={{ color: 'inherit', fontWeight: 600 }}>
            {notification.title}
          </Typography>
          {notification.message && (
            <Typography level="body-sm" sx={{ color: 'inherit', mt: 0.5 }}>
              {notification.message}
            </Typography>
          )}
        </Box>
      </Alert>
    </Box>
  );
};

const NotificationSnackbar: React.FC = () => {
  const { notifications, removeNotification } = useNotification();

  return (
    <>
      {notifications.map((notification, index) => (
        <NotificationItem
          key={notification.id}
          notification={notification}
          onClose={removeNotification}
          index={index}
        />
      ))}
    </>
  );
};

export default NotificationSnackbar;
